import React, { useEffect, useState } from "react";
import { effectLayerStyle } from "../lib/effectPresets";

const PRESETS = {
  warm: "linear-gradient(180deg, #FDFBF7 0%, #FFE8D6 35%, #FFD6BA 60%, #E8D5C4 80%, #DDE5C9 100%)",
  cool: "linear-gradient(180deg, #EAF4F4 0%, #CDE7E0 40%, #BFD8D5 70%, #A8C9C6 100%)",
  midnight: "linear-gradient(180deg, #0B1D3A 0%, #18305C 50%, #2D6A4F 100%)",
  candy: "linear-gradient(180deg, #FFE5EC 0%, #FFC2D1 40%, #FFAFCC 70%, #BDE0FE 100%)",
  forest: "linear-gradient(180deg, #DDE5C9 0%, #B7CE96 45%, #2D6A4F 100%)",
  gold: "linear-gradient(180deg, #FFF8E7 0%, #FFE7A0 50%, #FFD93D 100%)",
  aurora: "linear-gradient(180deg, #FDFBF7 0%, #E0C3FC 50%, #8EC5FC 100%)",
  sunset: "linear-gradient(180deg, #FFE0B5 0%, #FF9AA2 50%, #B5838D 100%)",
};

export const GRADIENT_PRESETS = PRESETS;

function vPct(pos) {
  if (!pos) return 50;
  if (pos.includes("bottom")) return 100;
  if (pos.includes("top")) return 0;
  const m = String(pos).match(/(\d+)%/);
  if (m) return Math.max(0, Math.min(100, parseInt(m[1], 10)));
  return 50;
}

export default function ChristmasBackdrop({
  bgUrl, gradient, customGradient, overlayOpacity,
  fit = "cover", position = "center", blur = 0, kenBurns = false,
  parallax = false, glass = false, blendMode = "normal", fixed = false,
  overlayColor = "#ffffff", topOffset = 0, overlays = [], preview = false, zoom = 1,
  effect = "none", effectOpacity = 50, effectBlend = "overlay", mobileHero = false, offsetY = 0,
}) {
  const grad = customGradient && customGradient.trim() ? customGradient : (PRESETS[gradient] || PRESETS.warm);
  const ov = overlayOpacity == null ? 40 : Math.max(0, Math.min(100, Number(overlayOpacity)));
  const blurPx = Math.max(0, Math.min(40, Number(blur) || 0));

  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    if (!parallax || !bgUrl) return;
    const onScroll = () => setScrollY(window.scrollY || 0);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [parallax, bgUrl]);

  const wrapperClass = `${fixed ? "fixed inset-0" : "absolute left-0 right-0"} -z-10 overflow-hidden pointer-events-none`;
  const wrapperStyle = fixed ? undefined : { top: -topOffset, bottom: 0 };

  const fitHeight = fit === "height";
  const transforms = [];
  if (fitHeight) transforms.push("translateX(-50%)");
  if (parallax) transforms.push(`translate3d(0, ${scrollY * 0.3}px, 0)`);
  const zz = Number(zoom) || 1;
  const baseScale = parallax ? 1.15 * zz : zz;
  if (baseScale !== 1) transforms.push(`scale(${baseScale})`);
  const offY = Number(offsetY) || 0;
  if (offY !== 0) transforms.push(`translateY(${offY}px)`);
  const imgStyle = {
    objectFit: fitHeight ? undefined : fit,
    objectPosition: fitHeight ? undefined : position,
    filter: blurPx > 0 ? `blur(${blurPx}px)` : undefined,
    mixBlendMode: blendMode !== "normal" ? blendMode : undefined,
    left: fitHeight ? "50%" : undefined,
    transform: transforms.length ? transforms.join(" ") : undefined,
    willChange: parallax ? "transform" : undefined,
  };
  const imgClass = `absolute ${fitHeight ? "top-0 h-full w-auto max-w-none" : (mobileHero ? "left-0 right-0 w-full top-0 h-[72vh] lg:!h-full lg:!top-0 lg:inset-0" : "inset-0 w-full h-full")} ${kenBurns ? "bg-ken-burns" : ""}`;

  return (
    <div className={wrapperClass} style={wrapperStyle} aria-hidden data-testid="christmas-backdrop">
      {bgUrl ? (
        <>
          {mobileHero && (
            <div className="lg:hidden absolute inset-0" aria-hidden
              style={{ background: "linear-gradient(180deg, #4a6fd6 0%, #243f8f 28%, #14245e 55%, #0B1D3A 100%)" }} />
          )}
          <img
            src={bgUrl}
            alt=""
            className={imgClass}
            style={imgStyle}
          />
          {glass && (
            <div className="absolute inset-0 backdrop-blur-md bg-white/10" style={{ WebkitBackdropFilter: "blur(12px)" }} />
          )}
          {ov > 0 && (
            <div className="absolute inset-0" style={{ background: overlayColor || "#ffffff", opacity: ov / 100 }} />
          )}
        </>
      ) : (
        <div className="absolute inset-0" style={{ background: grad }} />
      )}
      {effect && effect !== "none" && (
        <div className="absolute inset-0 overflow-hidden">
          <div data-testid="bg-effect-layer" style={effectLayerStyle(effect, effectOpacity, effectBlend)} />
        </div>
      )}
      {(overlays || []).map((o, i) => (
        (o && o.enabled !== false && o.url)
          ? <Overlay key={i} {...o} parentScrollY={scrollY} preview={preview} />
          : null
      ))}
    </div>
  );
}

function Overlay({ url, fit = "cover", position = "center", blend = "normal", blur = 0, opacity = 100, zoom = 1, kenBurns = false, parallax = false, animMode = "static", delay = 0, fade = 1, visible = 3, interval = 3, parentScrollY = 0, preview = false }) {
  const [shown, setShown] = useState(true);
  const [sy, setSy] = useState(0);

  useEffect(() => {
    if (parallax && !preview) {
      const onScroll = () => setSy(window.scrollY || 0);
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }
  }, [parallax, preview]);

  useEffect(() => {
    if (preview || animMode === "static") { setShown(true); return; }
    const timers = [];
    const d = (Number(delay) || 0) * 1000;
    const v = (Number(visible) || 3) * 1000;
    const iv = (Number(interval) || 3) * 1000;
    setShown(false);
    if (animMode === "delay") {
      timers.push(setTimeout(() => setShown(true), d));
    } else if (animMode === "once") {
      timers.push(setTimeout(() => setShown(true), d));
      timers.push(setTimeout(() => setShown(false), d + v));
    } else if (animMode === "cycle") {
      const run = (isOn) => {
        setShown(isOn);
        timers.push(setTimeout(() => run(!isOn), isOn ? v : iv));
      };
      timers.push(setTimeout(() => run(true), d));
    }
    return () => timers.forEach(clearTimeout);
  }, [animMode, delay, visible, interval, preview, url]);

  const blurPx = Math.max(0, Math.min(40, Number(blur) || 0));
  const z = Number(zoom) || 1;
  const isWidth = fit === "width";
  // Âncora vertical: 0 = topo, 100 = fundo, 50 = centro (relativa à imagem de fundo).
  const vp = vPct(position);
  const tParts = [];
  if (parallax) tParts.push(`translate3d(0, ${sy * 0.2}px, 0)`);
  // No modo "largura completa" a imagem tem altura natural: ancoramos o limite
  // superior (vp=0) ou inferior (vp=100) ao limite da imagem de fundo.
  if (isWidth) tParts.push(`translateY(-${vp}%)`);
  if (z !== 1) tParts.push(`scale(${parallax ? 1.15 * z : z})`);
  const style = {
    objectFit: isWidth ? undefined : fit,
    objectPosition: isWidth ? undefined : `center ${vp}%`,
    top: isWidth ? `${vp}%` : undefined,
    // O pivô do zoom coincide com a âncora, mantendo o limite alinhado ao fazer zoom in/out.
    transformOrigin: `center ${vp}%`,
    filter: blurPx > 0 ? `blur(${blurPx}px)` : undefined,
    mixBlendMode: blend !== "normal" ? blend : undefined,
    transform: tParts.length ? tParts.join(" ") : undefined,
    opacity: shown ? Math.max(0, Math.min(100, Number(opacity))) / 100 : 0,
    transition: `opacity ${Math.max(0, Number(fade) || 0)}s ease`,
  };
  const posCls = isWidth ? "left-0 right-0 w-full h-auto" : "inset-0 w-full h-full";
  return (
    <img src={url} alt="" data-testid="bg-overlay-layer"
      className={`absolute ${posCls} ${kenBurns ? "bg-ken-burns" : ""}`}
      style={style} />
  );
}
